import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BadgeCheck,
  Mail,
  Phone,
  Building2,
  Award,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  ShieldAlert,
  Upload,
} from "lucide-react";
import { useProfile } from "../../context/ProfileContext";
import {
  confirmarOTP,
  fetchResumenVerificaciones,
  solicitarOTP,
  solicitarVerificacionDocumento,
  solicitarVerificacionEmpresa,
  solicitarVerificacionProfesional,
} from "../../lib/verificaciones";
import { subirAdjunto, crearItem } from "../../lib/portafolio";
import { humanizeAxiosError } from "../../lib/api";

/* -------------------------------------------------------------------------- */
/*  Metadata visual                                                           */
/* -------------------------------------------------------------------------- */

const VERIFICACIONES = [
  {
    key: "correo",
    titulo: "Correo",
    descripcion: "Confirma tu correo con un código.",
    icon: Mail,
    metodo: "otp",
  },
  {
    key: "telefono",
    titulo: "Teléfono",
    descripcion: "Valida tu número con un código.",
    icon: Phone,
    metodo: "otp",
  },
  {
    key: "empresa",
    titulo: "Empresa",
    descripcion: "Registra tu RUC con evidencia (carga de constancia).",
    icon: Building2,
    metodo: "empresa",
  },
  {
    key: "profesional",
    titulo: "Colegio profesional",
    descripcion: "Sube tu constancia de colegiatura.",
    icon: Award,
    metodo: "profesional",
  },
];

const ESTADO_META = {
  aprobada: { label: "Verificado", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  en_revision: { label: "En revisión", tone: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  pendiente: { label: "Pendiente", tone: "bg-slate-50 text-slate-500 border-slate-200", Icon: BadgeCheck },
  rechazada: { label: "Rechazada", tone: "bg-red-50 text-red-700 border-red-200", Icon: ShieldAlert },
  expirada: { label: "Expirada", tone: "bg-slate-50 text-slate-500 border-slate-200", Icon: Clock },
};

/* -------------------------------------------------------------------------- */
/*  Modal OTP                                                                 */
/* -------------------------------------------------------------------------- */

function OTPModal({ open, tipo, onClose, onDone }) {
  const [stage, setStage] = useState("solicitar"); // solicitar | confirmar
  const [destino, setDestino] = useState("");
  const [codigo, setCodigo] = useState("");
  const [codigoDev, setCodigoDev] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setStage("solicitar");
      setDestino("");
      setCodigo("");
      setCodigoDev(null);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const label = tipo === "correo" ? "correo" : "teléfono";

  const handleSolicitar = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setLoading(true);
    try {
      const res = await solicitarOTP(tipo, destino || undefined);
      setDestino(res.enviado_a);
      setCodigoDev(res.codigo_dev);
      setStage("confirmar");
    } catch (err) {
      setError(humanizeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setLoading(true);
    try {
      await confirmarOTP(tipo, codigo);
      onDone();
    } catch (err) {
      setError(humanizeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`otp-modal-${tipo}`}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">
            Verificar {label}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
            data-testid="otp-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {stage === "solicitar" && (
          <form onSubmit={handleSolicitar} className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                {tipo === "correo" ? "Correo" : "Teléfono"} a verificar
              </label>
              <input
                type={tipo === "correo" ? "email" : "tel"}
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                placeholder={
                  tipo === "correo" ? "Se usará tu correo del perfil si lo dejas vacío" : "9 dígitos, ej. 999888777"
                }
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid={`otp-input-destino-${tipo}`}
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-red text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
              data-testid={`otp-btn-enviar-${tipo}`}
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Enviarme un código
            </button>
          </form>
        )}

        {stage === "confirmar" && (
          <form onSubmit={handleConfirmar} className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              Enviamos un código a <span className="font-semibold text-slate-900">{destino}</span>. Ingrésalo abajo. Expira en 10 minutos.
            </p>
            {codigoDev && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                <strong>Modo desarrollo:</strong> tu código es <span className="font-mono font-bold" data-testid="otp-codigo-dev">{codigoDev}</span>. En producción llegará por {label}.
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Código de 6 dígitos
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="000000"
                className="w-full px-3 py-3 border border-slate-200 rounded-lg text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid={`otp-input-codigo-${tipo}`}
                autoFocus
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStage("solicitar")}
                className="px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                data-testid={`otp-btn-reenviar-${tipo}`}
              >
                Reenviar
              </button>
              <button
                type="submit"
                disabled={loading || codigo.length < 4}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-red text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
                data-testid={`otp-btn-confirmar-${tipo}`}
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                Confirmar código
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal documento/empresa/profesional (evidencia)                           */
/* -------------------------------------------------------------------------- */

async function ensureEvidenciaItem(nombre) {
  // Los adjuntos viven bajo el módulo Portafolio (reusa storage). Creamos
  // un item privado técnico "Evidencias de verificación" si no existe.
  const item = await crearItem({
    titulo: nombre,
    categoria: "otro",
    visibilidad: "privada",
    destacado: false,
  });
  return item;
}

function EvidenciaModal({ open, tipo, onClose, onDone }) {
  const [payload, setPayload] = useState({});
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPayload({});
      setFile(null);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError("Adjunta una foto o PDF de evidencia");
      return;
    }
    setLoading(true);
    try {
      // 1) Subir archivo como adjunto en un item privado técnico
      const nombreItem = `Evidencia · ${tipo}`;
      const item = await ensureEvidenciaItem(nombreItem);
      const adj = await subirAdjunto(item.id, file);
      // 2) Crear solicitud de verificación
      const base = { ...payload, adjunto_id: adj.id };
      if (tipo === "documento") await solicitarVerificacionDocumento(base);
      else if (tipo === "empresa") await solicitarVerificacionEmpresa(base);
      else if (tipo === "profesional") await solicitarVerificacionProfesional(base);
      onDone();
    } catch (err) {
      setError(humanizeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  const campos = useMemo(() => {
    if (tipo === "documento")
      return [
        {
          name: "tipo_documento",
          label: "Tipo de documento",
          type: "select",
          options: [
            { value: "DNI", label: "DNI" },
            { value: "CE", label: "Carné de extranjería" },
            { value: "PASAPORTE", label: "Pasaporte" },
          ],
          required: true,
          default: "DNI",
        },
        { name: "numero_documento", label: "Número de documento", type: "text", required: true, maxLength: 20 },
      ];
    if (tipo === "empresa")
      return [
        { name: "razon_social", label: "Razón social", type: "text", required: true, maxLength: 200 },
        { name: "ruc", label: "RUC (11 dígitos)", type: "text", required: true, maxLength: 11, pattern: "[0-9]{11}" },
      ];
    return [
      { name: "colegio", label: "Colegio profesional", type: "text", required: true, maxLength: 120 },
      { name: "numero_registro", label: "Número de registro", type: "text", required: true, maxLength: 40 },
    ];
  }, [tipo]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`evidencia-modal-${tipo}`}
    >
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900 capitalize">
            Verificar {tipo}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
            data-testid="evidencia-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {campos.map((c) => (
            <div key={c.name}>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                {c.label} {c.required && "*"}
              </label>
              {c.type === "select" ? (
                <select
                  value={payload[c.name] || c.default || ""}
                  onChange={(e) => setPayload({ ...payload, [c.name]: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  data-testid={`evidencia-${tipo}-${c.name}`}
                  required={c.required}
                >
                  {c.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={c.type}
                  value={payload[c.name] || ""}
                  onChange={(e) => setPayload({ ...payload, [c.name]: e.target.value })}
                  maxLength={c.maxLength}
                  pattern={c.pattern}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  data-testid={`evidencia-${tipo}-${c.name}`}
                  required={c.required}
                />
              )}
            </div>
          ))}

          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Evidencia (JPG, PNG o PDF) *
            </label>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-blue/60 hover:bg-slate-50"
              data-testid={`evidencia-${tipo}-file-btn`}
            >
              <Upload size={16} />
              {file ? file.name : "Adjuntar archivo"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid={`evidencia-${tipo}-file-input`}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
            data-testid={`evidencia-${tipo}-cancelar`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-red text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid={`evidencia-${tipo}-enviar`}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Enviar para revisión
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Página principal                                                          */
/* -------------------------------------------------------------------------- */

export default function VerificacionesPage() {
  const { refresh: refreshProfile } = useProfile();
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState({ open: false, tipo: null });
  const [evid, setEvid] = useState({ open: false, tipo: null });

  const cargar = useCallback(async () => {
    try {
      const data = await fetchResumenVerificaciones();
      setResumen(data);
    } catch (e) {
      setError(humanizeAxiosError(e));
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await cargar();
      setLoading(false);
    })();
  }, [cargar]);

  const handleOtpDone = async () => {
    setOtp({ open: false, tipo: null });
    await cargar();
    await refreshProfile();
  };

  const handleEvidDone = async () => {
    setEvid({ open: false, tipo: null });
    await cargar();
    await refreshProfile();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-500" data-testid="verificaciones-loading">
        <Loader2 size={22} className="mx-auto animate-spin" />
        <p className="mt-2 text-sm">Cargando verificaciones…</p>
      </div>
    );
  }

  const nivel = resumen
    ? Math.round(
        (["correo", "telefono", "empresa", "profesional"]
          .map((k) => (resumen[k] === "aprobada" ? 1 : 0))
          .reduce((a, b) => a + b, 0) /
          4) *
          100
      )
    : 0;

  return (
    <div className="space-y-5" data-testid="verificaciones-page">
      <header className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <BadgeCheck size={20} className="text-brand-navy" />
              Verificaciones
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">
              Cada verificación añade una insignia a tu perfil y aumenta la confianza de otros usuarios en ConstruLink.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Nivel de confianza</p>
            <p className="text-2xl font-bold text-brand-navy mt-0.5" data-testid="verificaciones-nivel">{nivel}%</p>
          </div>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {VERIFICACIONES.map((v) => {
          const estado = resumen?.[v.key] || "pendiente";
          const meta = ESTADO_META[estado] || ESTADO_META.pendiente;
          const StateIcon = meta.Icon;
          const CardIcon = v.icon;
          const acciona = estado !== "aprobada" && estado !== "en_revision";
          return (
            <article
              key={v.key}
              className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col gap-3"
              data-testid={`verif-card-${v.key}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-blue/10 text-brand-navy flex items-center justify-center flex-shrink-0">
                  <CardIcon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-slate-900">{v.titulo}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{v.descripcion}</p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full border ${meta.tone}`}
                  data-testid={`verif-estado-${v.key}`}
                >
                  <StateIcon size={12} />
                  {meta.label}
                </span>
              </div>
              {acciona && (
                <button
                  type="button"
                  onClick={() =>
                    v.metodo === "otp"
                      ? setOtp({ open: true, tipo: v.key })
                      : setEvid({ open: true, tipo: v.key })
                  }
                  className="self-start px-3 py-1.5 rounded-lg bg-brand-red text-white text-xs font-bold hover:bg-red-700"
                  data-testid={`verif-btn-${v.key}`}
                >
                  {v.metodo === "otp" ? "Verificar con código" : "Enviar evidencia"}
                </button>
              )}
              {estado === "en_revision" && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 self-start">
                  Estamos revisando tu solicitud (máximo 48 horas hábiles).
                </p>
              )}
            </article>
          );
        })}
      </div>

      <OTPModal
        open={otp.open}
        tipo={otp.tipo}
        onClose={() => setOtp({ open: false, tipo: null })}
        onDone={handleOtpDone}
      />
      <EvidenciaModal
        open={evid.open}
        tipo={evid.tipo}
        onClose={() => setEvid({ open: false, tipo: null })}
        onDone={handleEvidDone}
      />
    </div>
  );
}
